import { Plan, PlanTierIndex } from "@jugl-web/rest-api";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";

export type PlanComparison = "upgrade" | "downgrade" | "current";

export type PlanActionLabelId =
  | "reactivate-plan"
  | "current-plan"
  | "go-yearly"
  | "go-monthly"
  | "contact-sales"
  | "upgrade"
  | "downgrade"
  | "get-started"
  | "start-for-free";

export type PlanActionState = {
  labelId: PlanActionLabelId;
  defaultLabel: string;
  variant: "primary" | "secondary";
  isDisabled: boolean;
  planComparison?: PlanComparison;
  isCurrentPlan: boolean;
  shouldShowGoYearly: boolean;
  shouldShowGoMonthly: boolean;
};

const LABEL_DEFAULTS: Record<PlanActionLabelId, string> = {
  "reactivate-plan": "Reactivate Plan",
  "current-plan": "Current Plan",
  "go-yearly": "Go yearly",
  "go-monthly": "Go monthly",
  "contact-sales": "Contact Sales",
  upgrade: "Upgrade",
  downgrade: "Downgrade",
  "get-started": "Get Started",
  "start-for-free": "Start For Free",
};

const isEnterprise = (plan: Plan): boolean =>
  // Enterprise plans are identified by the plan `type` meta field in existing
  // call sites. Fallback to name heuristic to mirror current PlanCard behavior.
  (plan.meta_data as { type?: string } | undefined)?.type === "enterprise" ||
  plan.name.toLowerCase().includes("enterprise");

const isFreePlan = (plan: Plan): boolean =>
  plan.prices.every((p) => p.unit_amount === 0);

/**
 * Single source of truth for the primary action button rendered per plan in
 * both `PlanCard` and `PlanComparison`. Encodes the label, variant, and
 * disabled state for every current/selected-interval combination.
 */
export const getPlanActionState = (args: {
  plan: Plan;
  currentPlanId?: string;
  currentInterval?: SubscriptionIntervalType;
  selectedInterval?: SubscriptionIntervalType;
  allPlans?: Plan[];
  isSubscriptionPendingCancel?: boolean;
  isMostPopular?: boolean;
}): PlanActionState => {
  const {
    plan,
    currentPlanId,
    currentInterval,
    selectedInterval,
    allPlans,
    isSubscriptionPendingCancel,
    isMostPopular = false,
  } = args;

  const isSamePlanId = !!currentPlanId && plan.id === currentPlanId;
  const isFree = isFreePlan(plan);

  const shouldShowGoYearly =
    isSamePlanId &&
    !isFree &&
    currentInterval === "month" &&
    selectedInterval === "year";
  const shouldShowGoMonthly =
    isSamePlanId &&
    !isFree &&
    currentInterval === "year" &&
    selectedInterval === "month";

  // Treat "current plan" only when the user is also viewing the interval they
  // are subscribed to (free plans have no interval). Mirrors the branch guard
  // previously applied in PlansStep so Go-Yearly / Go-Monthly win the label
  // race on interval mismatch.
  const isCurrentPlan =
    isSamePlanId &&
    (!currentInterval ||
      !selectedInterval ||
      selectedInterval === currentInterval ||
      isFree);

  const currentPlan =
    currentPlanId && allPlans
      ? allPlans.find((p) => p.id === currentPlanId)
      : undefined;
  const planComparison: PlanComparison | undefined =
    currentPlan && !isCurrentPlan
      ? comparePlanTiers(currentPlan, plan)
      : undefined;

  let labelId: PlanActionLabelId;
  if (isCurrentPlan && isSubscriptionPendingCancel) {
    labelId = "reactivate-plan";
  } else if (isCurrentPlan) {
    labelId = "current-plan";
  } else if (shouldShowGoYearly) {
    labelId = "go-yearly";
  } else if (shouldShowGoMonthly) {
    labelId = "go-monthly";
  } else if (isEnterprise(plan)) {
    labelId = "contact-sales";
  } else if (planComparison === "upgrade") {
    labelId = "upgrade";
  } else if (planComparison === "downgrade") {
    labelId = "downgrade";
  } else if (isFree) {
    labelId = "start-for-free";
  } else {
    labelId = "get-started";
  }

  const variant: "primary" | "secondary" =
    isMostPopular && !isCurrentPlan && planComparison !== "downgrade"
      ? "primary"
      : "secondary";

  const isDisabled =
    isCurrentPlan &&
    !isSubscriptionPendingCancel &&
    !shouldShowGoYearly &&
    !shouldShowGoMonthly;

  return {
    labelId,
    defaultLabel: LABEL_DEFAULTS[labelId],
    variant,
    isDisabled,
    planComparison,
    isCurrentPlan,
    shouldShowGoYearly,
    shouldShowGoMonthly,
  };
};

export const PAID_PLAN_TIERS: PlanTierIndex[] = [
  PlanTierIndex.starter,
  PlanTierIndex.pro,
  PlanTierIndex.business,
];

/**
 * Returns the plan tier index from meta_data.idx.
 * 0 = Free, 1 = Starter, 2 = Pro, 3 = Business.
 */
export const getPlanTierIndex = (plan: Plan): PlanTierIndex => {
  return plan.meta_data.idx;
};

/**
 * Compares the current plan tier against a target plan tier.
 */
export const comparePlanTiers = (
  currentPlan: Plan,
  targetPlan: Plan
): PlanComparison => {
  if (currentPlan.id === targetPlan.id) return "current";
  const currentIndex = getPlanTierIndex(currentPlan);
  const targetIndex = getPlanTierIndex(targetPlan);
  return targetIndex > currentIndex ? "upgrade" : "downgrade";
};

/**
 * Returns the next higher-tier plan relative to the current plan, or undefined
 * if the current plan is already the highest tier.
 */
export const getNextTierPlan = (
  currentPlan: Plan,
  allPlans: Plan[]
): Plan | undefined => {
  const sorted = [...allPlans].sort(
    (a, b) => a.meta_data.idx - b.meta_data.idx
  );
  const currentIndex = sorted.findIndex((p) => p.id === currentPlan.id);
  return currentIndex >= 0 && currentIndex < sorted.length - 1
    ? sorted[currentIndex + 1]
    : undefined;
};

/**
 * Checks whether the current plan is at or above a given plan name's tier.
 */
export const isCurrentPlanAtOrAbove = (
  currentPlan: Plan,
  targetPlanName: string,
  allPlans: Plan[]
): boolean => {
  const target = allPlans.find((p) => p.name === targetPlanName);
  if (!target) return false;
  const currentIndex = getPlanTierIndex(currentPlan);
  const targetIndex = getPlanTierIndex(target);
  return currentIndex >= targetIndex;
};
